<template>
    <l-marker-cluster-group :options="{ maxClusterRadius: 1 }">
        <l-marker v-for="photo in photos" :key="photo.photo_id" :title="photo.photo_id"
            :lat-lng="[photo.lat, photo.lon]" @contextmenu="() => getPhotoDetails(photo.photo_id)">
            <l-icon :icon-size="iconSize" :icon-anchor="iconAnchor" class-name="point-event">
                <div class="point-icon">
                    <div class="d-flex justify-content-center align-items-center"
                        style="width: 16px; height: 16px; background-color: white; border-radius: 15%; opacity: 0.8;">
                        <i class="bi bi-image" style="font-size: 16px; color: black;"></i>
                    </div>
                </div>
            </l-icon>
        </l-marker>
    </l-marker-cluster-group>
</template>

<script setup lang="ts">
import { LMarker, LIcon } from "@vue-leaflet/vue-leaflet"
import { LMarkerClusterGroup } from 'vue-leaflet-markercluster'
import { watch, onUnmounted } from 'vue'
import { usePhotos } from '@/composables/usePhotos'
import L from 'leaflet'

const props = defineProps<{
    photos: any[]
    isImageLibrary: boolean
    map: any
}>()

let imageOverlay: any = null

const iconSize: [number, number] = [16, 16]
const iconAnchor: [number, number] = [8, 8]

const { photoDetails, getPhotoDetails, parseCamPolygonCorners, createCustomImageOverlay } = usePhotos()

// Función alternativa con polígono para fallback
function createDebugPolygon(corners: [number, number][], imageUrl: string) {
    const polygon = L.polygon(corners, {
        color: '#ff6b35',
        fillColor: '#ff6b35',
        fillOpacity: 0.1,
        weight: 3,
        dashArray: '5, 5'
    })

    polygon.bindPopup(`
        <div style="text-align: center; max-width: 300px;">
            <h5 style="color: #333; margin-bottom: 10px;">📸 Vista de Cámara</h5>
            <img src="${imageUrl}" style="max-width: 250px; max-height: 180px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);" alt="Preview"/>
            <div style="margin-top: 10px;">
                <button onclick="window.open('${imageUrl}', '_blank')" 
                        style="padding: 10px 20px; background: #ff6b35; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">
                    🔍 Ver imagen completa
                </button>
            </div>
            <p style="font-size: 11px; color: #666; margin: 8px 0 0 0;">
                Esquinas: ${corners.length} puntos
            </p>
        </div>
    `, {
        maxWidth: 320
    })

    // Efectos hover
    polygon.on('mouseover', function (this: L.Polygon) {
        this.setStyle({
            fillOpacity: 0.3,
            weight: 4
        })
    })

    polygon.on('mouseout', function (this: L.Polygon) {
        this.setStyle({
            fillOpacity: 0.1,
            weight: 3
        })
    })

    return polygon
}

// Función principal para cargar el overlay
function loadImageOverlay() {
    try {
        if (!photoDetails.value) return

        const corners = parseCamPolygonCorners(photoDetails.value.cam_polygon)
        if (!corners || corners.length < 4) {
            console.error('No se pudieron obtener las esquinas del cam_polygon')
            return
        }

        let imageUrl = ''
        if (photoDetails.value.filename_jpg) {
            imageUrl = '/fserver/videos' +
                photoDetails.value.filename_jpg
                    .replace('Stills', 'stills')
                    .replace(/\\/g, '/')
        }

        if (!imageUrl) {
            console.error('No se pudo obtener la URL de la imagen')
            return
        }

        console.log('URL de imagen:', imageUrl)
        console.log('Esquinas para overlay:', corners)

        // Eliminar capa anterior
        if (imageOverlay && props.map?.leafletObject) {
            props.map.leafletObject.removeLayer(imageOverlay)
            imageOverlay = null
        }

        // Intentar crear custom overlay, fallback a polígono
        try {
            imageOverlay = createCustomImageOverlay(corners, imageUrl)
            console.log('Usando custom canvas overlay')
        } catch (canvasError) {
            console.warn('Error creando canvas overlay, usando polígono:', canvasError)
            imageOverlay = createDebugPolygon(corners, imageUrl)
        }

        if (imageOverlay) {
            imageOverlay.addTo(props.map.leafletObject)

            // Ajustar vista a las coordenadas
            const lats = corners.map(corner => corner[0])
            const lngs = corners.map(corner => corner[1])
            const bounds = L.latLngBounds(
                [Math.min(...lats), Math.min(...lngs)],
                [Math.max(...lats), Math.max(...lngs)]
            )

            props.map.leafletObject.fitBounds(bounds, {
                padding: [30, 30],
                maxZoom: 15
            })

            console.log('Image overlay agregado al mapa')
        }

    } catch (error) {
        console.error('Error en loadImageOverlay:', error)
    }
}

// Watch para cuando cambian los detalles de la foto
watch(
    () => photoDetails.value,
    (details) => {
        if (imageOverlay && props.map?.leafletObject) {
            props.map.leafletObject.removeLayer(imageOverlay)
            imageOverlay = null
        }

        if (props.isImageLibrary && details && details.cam_polygon && props.map?.leafletObject) {
            console.log('Cargando image overlay para photo_id:', details.photo_id)
            loadImageOverlay()
        }
    },
    { immediate: true }
)

// Limpiar al desmontar
onUnmounted(() => {
    if (imageOverlay && props.map?.leafletObject) {
        props.map.leafletObject.removeLayer(imageOverlay)
        imageOverlay = null
    }
})
</script>

<style scoped>
:deep(.custom-image-overlay) {
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
    transition: transform 0.2s ease;
}

:deep(.custom-image-overlay:hover) {
    transform: scale(1.01);
}
</style>
